// ============= Imports =============
import { useEffect } from 'react';
import { useSignalRHub } from '@/hooks/useSignalRHub';
import {
  createWorkspaceRelayHubHandlers,
  WorkspaceRelayHubOptions,
} from '@/handlers/signalR/workspaceRelayHub';
import { useToastSafe } from '@/hooks/signalR/useToastSafe';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';

// ============= Hook =============

/**
 * React hook that wires Workspace relay hub SignalR handlers into the current component.
 *
 * This hook subscribes to all Workspace-related SignalR events exposed by
 * createWorkspaceRelayHubHandlers and optionally filters by workspaceId.
 * It does not join the workspace SignalR group; that is done by WorkspaceRouteGuard
 * (or other callers) via addClientToGroupWorkspace so joins happen only once when authorized.
 *
 * @param options - Configuration for workspace relay hub handling (workspace filter, callbacks, logging, toasts).
 */

export const useWorkspaceRelayHubHandlers = (
  options: WorkspaceRelayHubOptions = {}
) => {
  const workspaceHub = useSignalRHub({ hubName: 'workspaceRelayHub' });
  const { handleError } = useWorkspaceErrorHandler(options.workspaceId);
  const enableToasts = options.enableToasts !== false; // Default to true
  const toasts = useToastSafe(enableToasts);

  useEffect(() => {
    if (!workspaceHub.isConnected || !workspaceHub.on || !workspaceHub.off) {
      return;
    }
    const { subscribe, unsubscribe } = createWorkspaceRelayHubHandlers(
      {
        on: workspaceHub.on,
        off: workspaceHub.off,
      },
      {
        ...options,
        toasts,
      }
    );

    try {
      subscribe();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('Failed to subscribe Workspace relay hub handlers:', error);
      handleError(error, 'Failed to subscribe to workspace updates');
    }

    return () => {
      unsubscribe();
    };
  }, [workspaceHub.isConnected, workspaceHub.on, workspaceHub.off, handleError, options.workspaceId, options.onDataRefresh, options.onMessageReceived, options.onEventReceived, options.getDisplayNameForUser, options.getWorkspaceNameById, options.currentUserId, options.enableToasts, options.enableLogging, enableToasts, toasts, options]);
};
